import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useTran } from "@/hooks";
import AppDialog from "@/components/common/AppDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useSurveyTakeStore, useSurveysStore } from "../hooks";
import {
  chunkSurveyQuestions,
  countAnsweredQuestions,
} from "../utils/survey-take";
import SurveyTake from "./SurveyTake";
import SurveyTakeHeader from "./SurveyTakeHeader";

const SurveyTakeDialog = observer(() => {
  const { t } = useTran();
  const { loadResponses } = useSurveysStore();
  const {
    open,
    survey,
    loading,
    error,
    close,
    responses,
    currentStep,
    viewMode,
  } = useSurveyTakeStore();

  const headerProps = useMemo(() => {
    const steps = chunkSurveyQuestions(survey?.questions || []);
    const currentQuestions = steps[currentStep]?.questions || [];
    const totalQuestions = survey?.questions?.length || 0;
    const answeredQuestions = countAnsweredQuestions(responses);
    const progress =
      totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
    return {
      title: survey?.title || t("Survey"),
      description: survey?.description,
      currentStep: currentStep + 1,
      totalSteps: steps.length || 1,
      category: steps[currentStep]?.category || "common",
      stepQuestions: currentQuestions.length,
      answeredQuestions,
      totalQuestions,
      progress,
      progressPercent: Math.round(progress),
    };
  }, [
    currentStep,
    responses,
    survey?.description,
    survey?.questions,
    survey?.title,
    t,
  ]);

  return (
    <AppDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
      title={survey?.title || t("Survey")}
      description={survey?.description}
      header={
        viewMode !== "result" ? <SurveyTakeHeader {...headerProps} /> : null
      }
      contentClassName="p-0"
      headerWrapperClassName="relative border-b border-border px-6 py-4 pb-4"
      headerClassName="w-full flex-1 gap-1"
      closeButtonClassName="absolute right-4 top-4"
      size="xl"
      bodyClassName="space-y-5 px-6 pt-4 pb-0"
    >
      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <SurveyTake
          survey={survey}
          showHeader={false}
          stickyFooter
          onSubmitted={() => {
            loadResponses();
            close();
          }}
        />
      )}
    </AppDialog>
  );
});

export default SurveyTakeDialog;
